
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function ArdenArcadeAreaPage() {
  // Apply SEO metadata; wrap in try/catch to avoid breaking the page if the hook fails
  try {
    useSEO({
      title:
        'Solar Panel Cleaning & Bird Proofing Arden-Arcade CA - Window Washing | Mr. EcoClean',
      description:
        'Top-rated solar panel cleaning, window washing, and bird proofing services in Arden-Arcade CA. Maximize solar efficiency, crystal clear windows, protect your investment. Eco-friendly methods, licensed & insured. Call (916) 836-6145 for free quote.',
      keywords:
        'solar panel cleaning Arden-Arcade, window washing Arden-Arcade, bird proofing Arden-Arcade, solar maintenance Arden-Arcade, residential window cleaning Arden-Arcade',
      canonical: '/areas/arden-arcade',
      ogType: 'website',
    });
  } catch (e) {
    console.error('SEO hook failed:', e);
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <h1
                  className="text-2xl font-bold text-red-600"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  Mr. EcoClean
                </h1>
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link
                to="/"
                className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                Home
              </Link>
              <Link
                to="/services"
                className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
              <Link
                to="/areas-covered"
                className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                Areas Covered
              </Link>
              <Link
                to="/about"
                className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                Contact
              </Link>
            </div>
            <div className="md:hidden">
              <button className="text-gray-700 hover:text-red-600 cursor-pointer">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/98a9b13d2e5e081b277a288ba5818f4e.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Solar Panel Cleaning & Bird Proofing in Arden-Arcade, CA
          </h2>
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
            Professional solar panel cleaning, window washing, and bird proofing for
            Arden-Arcade homes and businesses. Maximize your solar efficiency and
            protect your investment with eco-friendly methods.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Services for Arden-Arcade */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Our Arden-Arcade Services
            </h3>
            <p className="text-xl text-gray-600">
              Comprehensive solar panel cleaning, window washing, and bird proofing
              solutions for Arden-Arcade properties
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Solar Panel Cleaning */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-sun-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Solar Panel Cleaning
              </h4>
              <p className="text-gray-600 mb-6">
                Arden-Arcade's sunny climate makes solar panels a great investment.
                Keep them operating at peak efficiency with our professional cleaning
                service using deionized water. Remove dust, pollen, bird droppings, and
                debris that reduce energy production by up to 25%. Our gentle cleaning
                method protects your panels while maximizing output.
              </p>
              <Link
                to="/contact"
                className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer"
              >
                Get Quote →
              </Link>
            </div>

            {/* Window Washing */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-window-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Window Washing
              </h4>
              <p className="text-gray-600 mb-6">
                Crystal clear windows for Arden-Arcade homes and businesses. Our
                professional window washing service delivers streak‑free results every
                time. We clean interior and exterior windows, screens, tracks, and
                sills. Perfect for residential homes, commercial buildings, and
                storefronts. Enjoy brighter rooms and enhanced curb appeal with spotless
                windows.
              </p>
              <Link
                to="/contact"
                className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer"
              >
                Get Quote →
              </Link>
            </div>

            {/* Solar Bird Proofing */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Solar Bird Proofing
              </h4>
              <p className="text-gray-600 mb-6">
                Protect your solar investment from bird damage in Arden-Arcade.
                Birds nesting under solar panels cause serious problems: damaged
                wiring, fire hazards, reduced efficiency, and costly repairs. Our
                professional mesh installation creates a humane barrier that prevents
                nesting while allowing airflow. Includes cleanup of existing nests and
                droppings.
              </p>
              <Link
                to="/contact"
                className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer"
              >
                Get Quote →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                Why Arden-Arcade Chooses Mr. EcoClean
              </h3>
              <div className="space-y-6">
                {/* Local Business */}
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">
                      Local Arden-Arcade Business
                    </h5>
                    <p className="text-gray-600">
                      We know Arden-Arcade's climate, solar panel installations, and
                      unique cleaning needs. Fast response times throughout the area with
                      same‑day service available.
                    </p>
                  </div>
                </div>

                {/* Eco‑Friendly */}
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">
                      Eco-Friendly Methods
                    </h5>
                    <p className="text-gray-600">
                      Protecting the environment with biodegradable cleaning solutions,
                      deionized water systems, and water‑efficient techniques. Safe for
                      your family, pets, and landscaping.
                    </p>
                  </div>
                </div>

                {/* Licensed & Insured */}
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">
                      Licensed & Insured
                    </h5>
                    <p className="text-gray-600">
                      Fully licensed and insured for all solar panel cleaning, window
                      washing, and bird proofing services in Sacramento County. Your
                      property is protected.
                    </p>
                  </div>
                </div>

                {/* Satisfaction */}
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">
                      Satisfaction Guaranteed
                    </h5>
                    <p className="text-gray-600">
                      100% satisfaction guarantee on all services. We're not done until
                      you're happy with the results. Professional, reliable, and courteous
                      service every time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/1fe53895dbc344f81e4ae10fc5461665.jpeg"
                alt="Solar panel cleaning service in Arden-Arcade"
                className="rounded-xl shadow-lg w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Regular Solar Panel Cleaning in Arden-Arcade
            </h3>
            <p className="text-xl text-gray-600">
              Maximize your solar investment with professional maintenance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-flashlight-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">
                Increase Efficiency
              </h5>
              <p className="text-gray-600">
                Clean panels produce up to 25% more energy than dirty ones
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">
                Save Money
              </h5>
              <p className="text-gray-600">
                Lower energy bills and maximize your solar ROI
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">
                Extend Lifespan
              </h5>
              <p className="text-gray-600">
                Regular cleaning prevents damage and extends panel life
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-star-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">
                Protect Warranty
              </h5>
              <p className="text-gray-600">
                Maintain manufacturer warranty requirements
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">
            Ready to Clean Your Arden-Arcade Solar Panels?
          </h3>
          <p className="text-xl text-red-100 mb-8">
            Get a free quote for solar panel cleaning, window washing, or bird
            proofing in Arden-Arcade today. Fast, professional, eco‑friendly service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call: (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4
                className="text-2xl font-bold text-sky-400 mb-4"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Mr. EcoClean
              </h4>
              <p className="text-gray-300 mb-4">
                Professional solar panel cleaning, window washing, and bird proofing
                services for Sacramento and surrounding areas.
              </p>
              <div className="flex space-x-4">
                <i className="ri-facebook-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-instagram-line text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-google-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
              </div>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <Link to="/services" className="hover:text-sky-400 cursor-pointer">
                    Solar Panel Cleaning
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="hover:text-sky-400 cursor-pointer">
                    Window Washing
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="hover:text-sky-400 cursor-pointer">
                    Solar Bird Proofing
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">
                    (916) 836-6145
                  </a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">
                    Jason@mrecoclean.com
                  </a>
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Sacramento, CA
                </p>
              </div>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              © 2024 Mr. EcoClean. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-sky-400 cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
